/**
 * LINA Lens utilities — small helpers consolidated from hash-utils, url-validation,
 * content-limits, and routes.
 */

import { LENS_MAX_ANALYSIS_WORDS } from '@/lib/lens/file-types'

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

export async function hashToken(token: string): Promise<string> {
  const data = new TextEncoder().encode(token)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

// ---------------------------------------------------------------------------
// URL validation
// ---------------------------------------------------------------------------

/** Client-side URL validation for Lens URL input (fetch happens server-side). */
export function validateLensUrlInput(
  raw: string,
): { ok: true; url: string } | { ok: false; error: string } {
  const trimmed = raw.trim()
  if (!trimmed) {
    return { ok: false, error: 'Please enter a URL.' }
  }

  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
    const withScheme = hasScheme ? trimmed : `https://${trimmed}`
    const url = new URL(withScheme)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return { ok: false, error: 'Please enter a valid URL.' }
    }
    return { ok: true, url: url.toString() }
  } catch {
    return { ok: false, error: 'Please enter a valid URL.' }
  }
}

/**
 * Resolve an http(s) href from a raw URL or domain string.
 * Returns null when the value is empty or not a valid web URL.
 */
export function resolveHttpHref(raw?: string | null): string | null {
  if (!raw?.trim()) return null
  const result = validateLensUrlInput(raw)
  return result.ok ? result.url : null
}

/**
 * Resolve which URL(s) to analyze from the site page picker vs the entered URL.
 * Site selection is source of truth when the user has picked one or more pages.
 */
export function resolveLensAnalyzeSourceUrls(input: {
  activeUrl: string | null | undefined
  selectedUrls: Iterable<string>
}): { mode: 'none' } | { mode: 'single'; url: string } | { mode: 'multi'; urls: string[] } {
  const selected = Array.from(input.selectedUrls)
    .map((u) => u.trim())
    .filter(Boolean)
  if (selected.length > 1) return { mode: 'multi', urls: selected }
  if (selected.length === 1) return { mode: 'single', url: selected[0] }
  const fallback = input.activeUrl?.trim()
  if (fallback) return { mode: 'single', url: fallback }
  return { mode: 'none' }
}

// ---------------------------------------------------------------------------
// Content limits
// ---------------------------------------------------------------------------

export function countLensWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

export function isLensContentTooLong(wordCount: number): boolean {
  return wordCount > LENS_MAX_ANALYSIS_WORDS
}

export function canAnalyzeLensWordCount(wordCount: number): boolean {
  return wordCount > 0 && wordCount <= LENS_MAX_ANALYSIS_WORDS
}

export function formatLensWordCountLabel(wordCount: number): string {
  return `${wordCount.toLocaleString()} words`
}

export function formatLensContentTooLongMessage(wordCount: number): string {
  return `This content is ${wordCount.toLocaleString()} words. Lens supports up to ${LENS_MAX_ANALYSIS_WORDS.toLocaleString()} words — shorten it or paste a section instead.`
}

// ---------------------------------------------------------------------------
// Route helpers — token report access vs standalone chrome
// ---------------------------------------------------------------------------

/** B2B audit report opened via email link (?token=). No Supabase session required. */
export function isLensTokenAccessPath(pathname: string): boolean {
  return (
    pathname.startsWith('/lens/reports') ||
    pathname.startsWith('/lens/pilot-reports') ||
    pathname === '/lens/report' ||
    pathname.startsWith('/lens/report/')
  )
}

/**
 * Only tokenized Lens report links should use standalone chrome.
 * Main Lens app pages should render normal app header/footer.
 */
export function isLensStandalonePath(pathname: string): boolean {
  return isLensTokenAccessPath(pathname)
}
